import { useState, useEffect } from "react"
import api from "../api/adminApi"
import AdminLayout from "../components/AdminLayout"
import { STATIC_TC } from "../trust-center/data/staticContent"
import { useAdminFeedback } from "../context/AdminFeedbackContext"
import {
  normalizeComplianceItems,
  isLegacyComplianceConfig,
  migrateLegacyComplianceConfig,
  toComplianceWidgetItems,
} from "../trust-center/data/complianceConfig"


const WIDGET_META = {
  Compliances: { icon: "C", accent: "#00875A", accentBg: "#E3FCEF", label: "Compliances", desc: "Manage certifications shown on the Trust Center.", editable: true, removable: false },
  Controls: { icon: "S", accent: "#7C3AED", accentBg: "#F5F3FF", label: "Security Controls", desc: "Edit security control categories and their items.", editable: true, removable: false },
  FAQ: { icon: "Q", accent: "#B45309", accentBg: "#FEF3C7", label: "FAQ", desc: "Manage frequently asked questions shown on the FAQ page.", editable: true, removable: false },
  Subprocessors: { icon: "P", accent: "#0E7490", accentBg: "#ECFEFF", label: "Sub-processors", desc: "Manage third-party sub-processors shown on the Sub-processors page.", editable: true, removable: false },
}

const ADDABLE_WIDGETS = [
  { name: "Compliances", icon: "C", desc: "Compliance certifications grid" },
  { name: "Controls", icon: "S", desc: "Security control categories" },
  { name: "FAQ", icon: "Q", desc: "FAQ questions and answers" },
  { name: "Subprocessors", icon: "P", desc: "Third-party sub-processor list" },
]

const HIDDEN_WIDGETS = ["Overview", "TrustedBy", "Hero", "Resources"]

const T = {
  font: "'DarwinSans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  navy: "#002B54",
  blue: "#0183FF",
  blueHover: "#1558D6",
  border: "#DFE1E6",
  muted: "#6B778C",
  faint: "#97A0AF",
  bg: "#FAFBFC",
  green: "#00875A",
  greenBg: "#E3FCEF",
  greenDark: "#006644",
  red: "#DE350B",
  redBg: "#FFF0EE",
  redBorder: "#FFBDAD",
}

const st = {
  card: (visible) => ({
    background: "#fff",
    border: `1.5px solid ${visible ? T.border : "#EBECF0"}`,
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: visible ? "0 2px 10px rgba(12,35,64,.08)" : "none",
    opacity: visible ? 1 : 0.7,
    transition: "opacity .2s",
    fontFamily: T.font,
  }),
  row: (gap = 10) => ({ display: "flex", alignItems: "center", gap }),
  col: (gap = 12) => ({ display: "flex", flexDirection: "column", gap }),

  cardHd: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 22px", flexWrap: "wrap", gap: "12px",
  },
  iconBox: (bg, size = 40) => ({
    width: size, height: size, borderRadius: "10px", background: bg,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.47, flexShrink: 0,
  }),

  // toggle
  track: (on) => ({
    width: "38px", height: "20px", borderRadius: "100px",
    background: on ? T.blue : "#C1C7D0",
    position: "relative", cursor: "pointer", transition: "background .2s", flexShrink: 0,
  }),
  thumb: (on) => ({
    position: "absolute", top: "2px", left: on ? "20px" : "2px",
    width: "16px", height: "16px", borderRadius: "50%", background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,.25)", transition: "left .2s",
  }),

  // buttons
  btnBlue: (dis) => ({
    fontFamily: T.font, background: dis ? "#93c5fd" : T.blue, color: "#fff",
    border: "none", borderRadius: "100px", padding: "8px 20px",
    fontSize: "13px", fontWeight: 600, cursor: dis ? "not-allowed" : "pointer",
    letterSpacing: "-.1px", transition: "background .15s",
  }),
  btnGhost: {
    fontFamily: T.font, background: "transparent", color: "#344563",
    border: `1.5px solid #C1C7D0`, borderRadius: "100px", padding: "7px 16px",
    fontSize: "13px", fontWeight: 500, cursor: "pointer",
  },
  btnDanger: {
    fontFamily: T.font, background: T.redBg, color: T.red,
    border: `1.5px solid ${T.redBorder}`, borderRadius: "8px", padding: "6px 12px",
    fontSize: "12px", fontWeight: 600, cursor: "pointer",
  },
  btnAdd: {
    fontFamily: T.font, background: "#EBF2FF", color: T.blue,
    border: "1.5px solid #BFDBFE", borderRadius: "8px", padding: "7px 14px",
    fontSize: "12.5px", fontWeight: 600, cursor: "pointer", flexShrink: 0,
    whiteSpace: "nowrap",
  },

  edBody: {
    padding: "20px 22px", background: T.bg, borderTop: `1.5px solid #EBECF0`,
  },
  sectionLabel: {
    fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
    letterSpacing: ".07em", color: T.muted, marginBottom: "14px",
    display: "flex", alignItems: "center", gap: "6px",
  },

  input: {
    fontFamily: T.font, fontSize: "13px", border: `1.5px solid ${T.border}`,
    borderRadius: "8px", padding: "8px 12px", color: T.navy,
    background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
  },

  tag: {
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: "#F0F3F8", border: `1px solid ${T.border}`,
    borderRadius: "100px", padding: "4px 10px 4px 12px",
    fontSize: "12px", color: "#344563", fontWeight: 500,
  },
  tagX: {
    background: "none", border: "none", padding: "0", lineHeight: 1,
    cursor: "pointer", color: T.faint, fontSize: "15px", fontWeight: 700,
    display: "flex", alignItems: "center",
  },

  hiddenBanner: {
    padding: "12px 22px", background: "#F5F7FA", borderTop: `1px solid #EBECF0`,
    fontSize: "12.5px", color: T.faint, fontStyle: "italic",
    display: "flex", alignItems: "center", gap: "6px",
  },
  readonlyBanner: {
    padding: "12px 22px", background: T.bg, borderTop: `1px solid #EBECF0`,
    fontSize: "12.5px", color: T.muted, display: "flex", alignItems: "center", gap: "6px",
  },
}

/* ══════════════════════════════════════════════════
   SMALL ATOMS
══════════════════════════════════════════════════ */
function Toggle({ on, onChange }) {
  return (
    <div style={{ ...st.row(8), cursor: "pointer", userSelect: "none" }} onClick={onChange}>
      <div style={st.track(on)}><div style={st.thumb(on)} /></div>
      <span style={{ fontSize: "13px", fontWeight: 600, color: on ? T.navy : T.faint }}>
        {on ? "Visible" : "Hidden"}
      </span>
    </div>
  )
}

function TagPill({ item, onRemove, onClick }) {
  const label = typeof item === "string" ? item : item.label
  const desc = typeof item === "object" ? item.description : ""
  return (
    <span
      style={{ ...st.tag, cursor: onClick ? "pointer" : "default", border: onClick ? `1px solid ${T.blue}` : st.tag.border }}
      title={desc || "Click to edit"}
      onClick={onClick}
    >
      {label}
      <button style={st.tagX} onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>
    </span>
  )
}

/* ══════════════════════════════════════════════════
   FAQ EDITOR
══════════════════════════════════════════════════ */
function FaqEditor({ config, onChange }) {
  const items = config?.items?.length ? config.items : [...STATIC_TC.faqs]
  const [editingIdx, setEditingIdx] = useState(null)
  const [question, setQuestion] = useState("")
  const [answer, setAnswer] = useState("")

  const updateItems = (next) => onChange({ ...config, items: next })

  const startAdd = () => {
    setEditingIdx(items.length)
    setQuestion("")
    setAnswer("")
  }

  const startEdit = (idx) => {
    setEditingIdx(idx)
    setQuestion(items[idx].q || items[idx].question || "")
    setAnswer(items[idx].a || items[idx].answer || "")
  }

  const cancelEdit = () => {
    setEditingIdx(null)
    setQuestion("")
    setAnswer("")
  }

  const saveItem = () => {
    if (!question.trim() || !answer.trim()) return
    const entry = { q: question.trim(), a: answer.trim() }
    const next = [...items]
    if (editingIdx !== null && editingIdx < items.length) {
      next[editingIdx] = entry
    } else {
      next.push(entry)
    }
    updateItems(next)
    cancelEdit()
  }

  const removeItem = (idx) => {
    updateItems(items.filter((_, i) => i !== idx))
    if (editingIdx === idx) cancelEdit()
  }

  const renderForm = (isAdd) => (
    <div style={{ ...st.col(8), background: "#fff", border: `1.5px solid ${T.blue}`, borderRadius: "10px", padding: "14px 16px" }}>
      <div style={{ fontSize: "12px", fontWeight: 700, color: T.navy }}>
        {isAdd ? "Add FAQ" : "Edit FAQ"}
      </div>
      <input
        style={st.input}
        placeholder="Question"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
      />
      <textarea
        style={{ ...st.input, height: "80px", minHeight: "80px", maxHeight: "80px", resize: "none", overflowY: "auto" }}
        placeholder="Answer"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
      />
      <div style={st.row(8)}>
        <button type="button" style={st.btnBlue(false)} onClick={saveItem}>Save</button>
        <button type="button" style={st.btnGhost} onClick={cancelEdit}>Cancel</button>
      </div>
    </div>
  )

  return (
    <div style={st.col(14)}>
      <div style={st.sectionLabel}>{items.length} FAQ items</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {items.map((item, idx) => (
          editingIdx === idx ? (
            <div key={idx}>{renderForm(false)}</div>
          ) : (
            <div
              key={idx}
              style={{
                background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: "10px",
                padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: T.navy }}>{item.q}</div>
                  <div style={{ fontSize: "12.5px", color: T.muted, marginTop: 4, lineHeight: 1.5 }}>{item.a}</div>
                </div>
                <div style={{ ...st.row(6), flexShrink: 0 }}>
                  <button type="button" style={st.btnGhost} onClick={() => startEdit(idx)}>Edit</button>
                  <button type="button" style={st.btnDanger} onClick={() => removeItem(idx)}>Remove</button>
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {editingIdx === items.length ? (
        renderForm(true)
      ) : editingIdx === null ? (
        <button type="button" style={{ ...st.btnAdd, alignSelf: "flex-start" }} onClick={startAdd}>
          + Add FAQ
        </button>
      ) : null}
    </div>
  )
}

function SubprocessorsEditor({ config, onChange }) {
  const { toast } = useAdminFeedback()
  const items = config?.items?.length ? config.items : [...STATIC_TC.subprocessors]
  const [editingIdx, setEditingIdx] = useState(null)
  const [name, setName] = useState("")
  const [purpose, setPurpose] = useState("")
  const [location, setLocation] = useState("")
  const [logoUrl, setLogoUrl] = useState("")
  const [uploading, setUploading] = useState(false)

  const updateItems = (next) => onChange({ ...config, items: next })

  const resetForm = () => {
    setEditingIdx(null)
    setName("")
    setPurpose("")
    setLocation("")
    setLogoUrl("")
  }

  const startAdd = () => {
    setEditingIdx(items.length)
    setName("")
    setPurpose("")
    setLocation("")
    setLogoUrl("")
  }

  const startEdit = (idx) => {
    setEditingIdx(idx)
    setName(items[idx].name || "")
    setPurpose(items[idx].purpose || "")
    setLocation(items[idx].location || "")
    setLogoUrl(items[idx].logoUrl || "")
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    try {
      setUploading(true)
      const formData = new FormData()
      formData.append("file", file)
      const res = await api.post("/widgets/upload-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      setLogoUrl(res.data.url)
    } catch {
      toast.error("Icon upload failed")
    } finally {
      setUploading(false)
    }
  }

  const saveItem = () => {
    if (!name.trim() || !purpose.trim()) return
    const entry = {
      name: name.trim(),
      purpose: purpose.trim(),
      location: location.trim(),
      logoUrl: logoUrl.trim(),
    }
    const next = [...items]
    if (editingIdx !== null && editingIdx < items.length) {
      next[editingIdx] = entry
    } else {
      next.push(entry)
    }
    updateItems(next)
    resetForm()
  }

  const removeItem = (idx) => {
    updateItems(items.filter((_, i) => i !== idx))
    if (editingIdx === idx) resetForm()
  }

  const renderForm = (isAdd) => (
    <div style={{ ...st.col(8), background: "#fff", border: `1.5px solid ${T.blue}`, borderRadius: "10px", padding: "14px 16px" }}>
      <div style={{ fontSize: "12px", fontWeight: 700, color: T.navy }}>
        {isAdd ? "Add sub-processor" : "Edit sub-processor"}
      </div>
      <input style={st.input} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <input style={st.input} placeholder="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      <input style={st.input} placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
      <div style={st.col(5)}>
        <label style={{ fontSize: "11px", fontWeight: 700, color: T.muted, textTransform: "uppercase" }}>Icon (optional)</label>
        <div style={st.row(10)}>
          <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ fontSize: "12px" }} disabled={uploading} />
          {uploading && <span style={{ fontSize: "11px", color: T.blue }}>Uploading…</span>}
          {logoUrl && <span style={{ fontSize: "11px", color: T.green }}>Ready</span>}
        </div>
        <div style={{ fontSize: "11px", color: T.faint, lineHeight: 1.5 }}>
          Upload a company logo, or leave blank to use the default sub-processor icon.
        </div>
        {logoUrl && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
            <img src={logoUrl} alt="" style={{ width: 38, height: 38, objectFit: "contain" }} />
            <button type="button" style={st.btnGhost} onClick={() => setLogoUrl("")}>Remove icon</button>
          </div>
        )}
      </div>
      <div style={st.row(8)}>
        <button type="button" style={st.btnBlue(uploading)} onClick={saveItem} disabled={uploading}>Save</button>
        <button type="button" style={st.btnGhost} onClick={resetForm}>Cancel</button>
      </div>
    </div>
  )

  return (
    <div style={st.col(14)}>
      <div style={st.sectionLabel}>{items.length} sub-processors</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {items.map((item, idx) => (
          editingIdx === idx ? (
            <div key={`${item.name}-${idx}`}>{renderForm(false)}</div>
          ) : (
            <div
              key={`${item.name}-${idx}`}
              style={{
                background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: "10px",
                padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                <div style={{ display: "flex", gap: 12, flex: 1, minWidth: 0 }}>
                  {item.logoUrl ? (
                    <img src={item.logoUrl} alt="" style={{ width: 38, height: 38, objectFit: "contain", flexShrink: 0 }} />
                  ) : (
                    <span style={{ width: 38, height: 38, display: "grid", placeItems: "center", flexShrink: 0, fontSize: 12, fontWeight: 800, color: T.muted, background: "#F4F5F7", borderRadius: 8 }}>SP</span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13.5px", fontWeight: 700, color: T.navy }}>{item.name}</div>
                    <div style={{ fontSize: "12.5px", color: T.muted, marginTop: 4, lineHeight: 1.5 }}>{item.purpose}</div>
                    <div style={{ fontSize: "12px", color: T.faint, marginTop: 4 }}>{item.location || "—"}</div>
                  </div>
                </div>
                <div style={{ ...st.row(6), flexShrink: 0 }}>
                  <button type="button" style={st.btnGhost} onClick={() => startEdit(idx)}>Edit</button>
                  <button type="button" style={st.btnDanger} onClick={() => removeItem(idx)}>Remove</button>
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {editingIdx === items.length ? (
        renderForm(true)
      ) : editingIdx === null ? (
        <button type="button" style={{ ...st.btnAdd, alignSelf: "flex-start" }} onClick={startAdd}>
          + Add sub-processor
        </button>
      ) : null}
    </div>
  )
}

/* ══════════════════════════════════════════════════
   COMPLIANCES EDITOR
══════════════════════════════════════════════════ */
function CompliancesEditor({ config, onChange }) {
  const { toast } = useAdminFeedback()
  const items = normalizeComplianceItems(config)
  const [editingIdx, setEditingIdx] = useState(null)
  const [short, setShort] = useState("")
  const [body, setBody] = useState("")
  const [desc, setDesc] = useState("")
  const [url, setUrl] = useState("")
  const [logoUrl, setLogoUrl] = useState("")
  const [status, setStatus] = useState("active")
  const [uploading, setUploading] = useState(false)
  const DEFAULT_CERT_ICON = "ribbon-star"

  useEffect(() => {
    if (isLegacyComplianceConfig(config)) {
      onChange({ items: toComplianceWidgetItems(migrateLegacyComplianceConfig(config)) })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const persist = (nextItems) => onChange({ items: toComplianceWidgetItems(nextItems) })

  const resetForm = () => {
    setEditingIdx(null)
    setShort("")
    setBody("")
    setDesc("")
    setUrl("")
    setLogoUrl("")
    setStatus("active")
  }

  const startAdd = () => {
    setEditingIdx(items.length)
    setShort("")
    setBody("")
    setDesc("")
    setUrl("")
    setLogoUrl("")
    setStatus("active")
  }

  const startEdit = (idx) => {
    const item = items[idx]
    setEditingIdx(idx)
    setShort(item.short || "")
    setBody(item.body || "")
    setDesc(item.desc || "")
    setUrl(item.url || "")
    setLogoUrl(item.logoUrl || "")
    setStatus(item.status || "active")
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    try {
      setUploading(true)
      const formData = new FormData()
      formData.append("file", file)
      const res = await api.post("/widgets/upload-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      setLogoUrl(res.data.url)
    } catch {
      toast.error("Logo upload failed")
    } finally {
      setUploading(false)
    }
  }

  const saveItem = () => {
    if (!short.trim()) return
    const id = editingIdx !== null && editingIdx < items.length
      ? items[editingIdx].id
      : `cert-${Date.now()}`
    const entry = {
      id,
      short: short.trim(),
      name: short.trim(),
      body: body.trim(),
      desc: desc.trim(),
      url: url.trim(),
      icon: DEFAULT_CERT_ICON,
      logoUrl: logoUrl.trim(),
      svg: logoUrl.trim() ? "" : "",
      group: "Certification",
      status,
    }
    const next = [...items]
    if (editingIdx !== null && editingIdx < items.length) {
      next[editingIdx] = entry
    } else {
      next.push(entry)
    }
    persist(next)
    resetForm()
  }

  const removeItem = (idx) => {
    persist(items.filter((_, i) => i !== idx))
    if (editingIdx === idx) resetForm()
  }

  const statusLabel = (value) => {
    if (value === "pending") return "Pending"
    if (value === "in_progress") return "In progress"
    if (value === "inactive") return "Inactive"
    return "Active"
  }

  const renderForm = (isAdd) => (
    <div style={{ ...st.col(8), background: "#fff", border: `1.5px solid ${T.blue}`, borderRadius: "10px", padding: "14px 16px" }}>
      <div style={{ fontSize: "12px", fontWeight: 700, color: T.navy }}>
        {isAdd ? "Add certification" : "Edit certification"}
      </div>
      <input style={st.input} placeholder="Heading (e.g. CPRA)" value={short} onChange={(e) => setShort(e.target.value)} />
      <input style={st.input} placeholder="Subtitle / issuer (e.g. State of California)" value={body} onChange={(e) => setBody(e.target.value)} />
      <textarea
        style={{ ...st.input, height: "72px", minHeight: "72px", maxHeight: "72px", resize: "none", overflowY: "auto" }}
        placeholder="Description shown on the card"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      <input style={st.input} placeholder="Detail page URL (optional)" value={url} onChange={(e) => setUrl(e.target.value)} />
      <div style={st.col(5)}>
        <label style={{ fontSize: "11px", fontWeight: 700, color: T.muted, textTransform: "uppercase" }}>Status</label>
        <select style={st.input} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">Active (no tag)</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div style={st.col(5)}>
        <label style={{ fontSize: "11px", fontWeight: 700, color: T.muted, textTransform: "uppercase" }}>Logo (optional)</label>
        <div style={st.row(10)}>
          <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ fontSize: "12px" }} />
          {uploading && <span style={{ fontSize: "11px", color: T.blue }}>Uploading…</span>}
          {logoUrl && <span style={{ fontSize: "11px", color: T.green }}>Ready</span>}
        </div>
      </div>
      <div style={st.row(8)}>
        <button type="button" style={st.btnBlue(uploading)} onClick={saveItem} disabled={uploading}>Save</button>
        <button type="button" style={st.btnGhost} onClick={resetForm}>Cancel</button>
      </div>
    </div>
  )

  return (
    <div style={st.col(14)}>
      <div style={st.sectionLabel}>{items.length} certification{items.length === 1 ? "" : "s"}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {items.map((item, idx) => (
          editingIdx === idx ? (
            <div key={item.id}>{renderForm(false)}</div>
          ) : (
            <div
              key={item.id}
              style={{
                background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: "10px",
                padding: "14px 16px", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: T.navy }}>{item.short}</div>
                  {item.status && item.status !== "active" && (
                    <span style={{
                      fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                      padding: "3px 8px", borderRadius: "999px",
                      background: item.status === "pending" ? "#EBF2FF" : item.status === "inactive" ? "#F4F5F7" : "#FFFAE6",
                      color: item.status === "pending" ? "#0052CC" : item.status === "inactive" ? "#6B778C" : "#FF8B00",
                    }}>
                      {statusLabel(item.status)}
                    </span>
                  )}
                </div>
                {item.body && <div style={{ fontSize: "12px", color: T.muted, marginTop: 4 }}>{item.body}</div>}
                {item.desc && <div style={{ fontSize: "12.5px", color: T.muted, marginTop: 6, lineHeight: 1.5 }}>{item.desc}</div>}
              </div>
              <div style={{ ...st.row(6), flexShrink: 0 }}>
                <button type="button" style={st.btnGhost} onClick={() => startEdit(idx)}>Edit</button>
                <button type="button" style={st.btnDanger} onClick={() => removeItem(idx)}>Remove</button>
              </div>
            </div>
          )
        ))}
      </div>

      {editingIdx === items.length ? (
        renderForm(true)
      ) : editingIdx === null ? (
        <button type="button" style={{ ...st.btnAdd, alignSelf: "flex-start" }} onClick={startAdd}>
          + Add certification
        </button>
      ) : null}
    </div>
  )
}

/* ══════════════════════════════════════════════════
   TRUSTED BY EDITOR
══════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════
   CONTROLS EDITOR
══════════════════════════════════════════════════ */
function ControlsEditor({ config, onChange }) {
  const cats = config?.categories || []
  const [showAddCat, setShowAddCat] = useState(false)
  const [newTitle, setNewTitle] = useState("")
  const [newBlurb, setNewBlurb] = useState("")
  const [newIcon, setNewIcon] = useState("")
  const [newBg, setNewBg] = useState("#EBF2FF")

  const updateCats = (next) => onChange({ ...config, categories: next })
  const [addingToIdx, setAddingToIdx] = useState(null)
  const [editingItemIdx, setEditingItemIdx] = useState(null)
  const [itemLabel, setItemLabel] = useState("")
  const [itemDesc, setItemDesc] = useState("")

  const addItem = (idx) => {
    if (!itemLabel.trim()) return
    const next = [...cats]
    const newItem = { label: itemLabel.trim(), description: itemDesc.trim() }

    if (editingItemIdx !== null) {
      const up = [...(next[idx].items || [])]
      up[editingItemIdx] = newItem
      next[idx] = { ...next[idx], items: up }
    } else {
      next[idx] = { ...next[idx], items: [...(next[idx].items || []), newItem] }
    }

    updateCats(next)
    cancelItem()
  }

  const cancelItem = () => {
    setItemLabel(""); setItemDesc(""); setAddingToIdx(null); setEditingItemIdx(null)
  }

  const editItem = (ci, ii, item) => {
    setAddingToIdx(ci)
    setEditingItemIdx(ii)
    setItemLabel(typeof item === "string" ? item : item.label)
    setItemDesc(typeof item === "object" ? item.description : "")
  }

  const removeItem = (ci, ii) => {
    const next = [...cats]
    next[ci] = { ...next[ci], items: next[ci].items.filter((_, i) => i !== ii) }
    updateCats(next)
    if (editingItemIdx === ii && addingToIdx === ci) cancelItem()
  }
  const removeCat = (i) => updateCats(cats.filter((_, idx) => idx !== i))

  const updateCat = (idx, patch) => {
    const next = [...cats]
    next[idx] = { ...next[idx], ...patch }
    updateCats(next)
  }

  const addCategory = () => {
    if (!newTitle.trim()) return
    updateCats([...cats, { title: newTitle.trim(), blurb: newBlurb.trim(), icon: newIcon, bg: newBg, items: [] }])
    setNewTitle(""); setNewBlurb(""); setNewIcon(""); setNewBg("#EBF2FF"); setShowAddCat(false)
  }

  const startAddItem = (ci) => {
    setAddingToIdx(ci)
    setEditingItemIdx(null)
    setItemLabel("")
    setItemDesc("")
  }

  const renderControlItemForm = (ci) => (
    <div style={{
      width: "100%", flexBasis: "100%",
      ...st.col(8), background: "#f8fafc", padding: "12px", borderRadius: "8px", border: `1px dashed ${T.border}`,
    }}>
      <input
        style={st.input}
        placeholder="Control Name (e.g. AWS)"
        value={itemLabel}
        onChange={e => setItemLabel(e.target.value)}
        autoFocus
      />
      <textarea
        style={{ ...st.input, height: "60px", minHeight: "60px", maxHeight: "60px", resize: "none", overflowY: "auto" }}
        placeholder="Explanation (How it's done...)"
        value={itemDesc}
        onChange={e => setItemDesc(e.target.value)}
      />
      <div style={st.row(8)}>
        <button style={{ ...st.btnBlue(false), padding: "5px 15px", fontSize: "11px" }} onClick={() => addItem(ci)}>
          {editingItemIdx !== null ? "Update Item" : "Add Item"}
        </button>
        <button style={{ ...st.btnGhost, padding: "4px 12px", fontSize: "11px" }} onClick={cancelItem}>Cancel</button>
      </div>
    </div>
  )

  return (
    <div style={st.col(16)}>
      <div style={st.sectionLabel}>{cats.length} security categories</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "14px" }}>
        {cats.map((cat, ci) => (
          <div key={cat.title + ci} style={{
            background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: "12px",
            padding: "18px", display: "flex", flexDirection: "column", gap: "12px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
              <div style={{ ...st.row(10), flex: 1, minWidth: 0 }}>
                <div style={{
                  width: "32px", height: "32px", borderRadius: "8px", background: cat.bg || "#EBF2FF",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: "15px", flexShrink: 0,
                }}>{cat.icon || "•"}</div>
                <input
                  style={{
                    ...st.input,
                    flex: 1,
                    minWidth: 0,
                    fontSize: "13.5px",
                    fontWeight: 700,
                    padding: "6px 10px",
                  }}
                  placeholder="Category heading"
                  value={cat.title || ""}
                  onChange={(e) => updateCat(ci, { title: e.target.value })}
                />
              </div>
              <button style={st.btnDanger} onClick={() => removeCat(ci)}>Remove</button>
            </div>
            <textarea
              style={{ ...st.input, height: "52px", minHeight: "52px", maxHeight: "52px", resize: "none", overflowY: "auto", fontSize: "12px" }}
              placeholder="Category description (shown on overview cards)"
              value={cat.blurb || cat.description || ""}
              onChange={e => updateCat(ci, { blurb: e.target.value })}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", minHeight: "28px", alignItems: "flex-start" }}>
              {(cat.items || []).length === 0 && addingToIdx !== ci
                ? <span style={{ fontSize: "12px", color: T.faint, fontStyle: "italic" }}>No items yet</span>
                : (cat.items || []).map((item, ii) => {
                  if (addingToIdx === ci && editingItemIdx === ii) {
                    return <div key={`edit-${ci}-${ii}`} style={{ width: "100%", flexBasis: "100%" }}>{renderControlItemForm(ci)}</div>
                  }
                  return (
                    <TagPill
                      key={ii}
                      item={item}
                      onRemove={() => removeItem(ci, ii)}
                      onClick={() => editItem(ci, ii, item)}
                    />
                  )
                })
              }
              {addingToIdx === ci && editingItemIdx === null && renderControlItemForm(ci)}
            </div>

            {addingToIdx !== ci && (
              <button
                style={{ ...st.btnAdd, fontSize: "11px", padding: "5px 12px" }}
                onClick={() => startAddItem(ci)}
              >
                + Add Control Item
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add Element */}
      {showAddCat ? (
        <div style={{ background: "#fff", border: `1.5px solid ${T.blue}`, borderRadius: "10px", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ fontSize: "12px", fontWeight: 700, color: T.navy }}>Add New Category</div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <input style={{ ...st.input, flex: "2 1 160px" }} placeholder="Category title" value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onFocus={e => e.target.style.borderColor = T.blue} onBlur={e => e.target.style.borderColor = T.border}
              onKeyDown={e => e.key === "Enter" && addCategory()} />
            <input style={{ ...st.input, flex: "0 1 70px" }} placeholder="Icon" value={newIcon}
              onChange={e => setNewIcon(e.target.value)}
              onFocus={e => e.target.style.borderColor = T.blue} onBlur={e => e.target.style.borderColor = T.border} />
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <label style={{ fontSize: "11px", fontWeight: 600, color: T.muted, whiteSpace: "nowrap" }}>BG color</label>
              <input type="color" value={newBg} onChange={e => setNewBg(e.target.value)}
                style={{ width: "36px", height: "32px", border: `1.5px solid ${T.border}`, borderRadius: "6px", cursor: "pointer", padding: "2px" }} />
            </div>
          </div>
          <textarea
            style={{ ...st.input, height: "56px", minHeight: "56px", maxHeight: "56px", resize: "none", overflowY: "auto" }}
            placeholder="Category description (shown on overview cards)"
            value={newBlurb}
            onChange={e => setNewBlurb(e.target.value)}
          />
          <div style={st.row(8)}>
            <button style={st.btnBlue(false)} onClick={addCategory}>Add</button>
            <button style={st.btnGhost} onClick={() => { setShowAddCat(false); setNewTitle("") }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button
          style={{ ...st.btnAdd, alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "6px" }}
          onClick={() => setShowAddCat(true)}
        >
          + Add Element
        </button>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════
   ADD WIDGET MODAL
══════════════════════════════════════════════════ */
function AddWidgetModal({ existingNames, onAdd, onClose }) {
  const [selected, setSelected] = useState(null)
  const available = ADDABLE_WIDGETS.filter(w => !existingNames.includes(w.name))

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(12,35,64,.55)",
        backdropFilter: "blur(5px)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
      }}
    >
      <div style={{
        background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "440px",
        boxShadow: "0 24px 64px rgba(12,35,64,.2)", fontFamily: T.font, overflow: "hidden",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: `1.5px solid #EBECF0`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "15px", fontWeight: 700, color: T.navy }}>Add a Widget</span>
          <button style={{ ...st.tagX, fontSize: "20px", color: T.muted }} onClick={onClose}>×</button>
        </div>
        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {available.length === 0
            ? <p style={{ fontSize: "13px", color: T.muted, textAlign: "center", padding: "16px 0" }}>All available widgets are already added.</p>
            : available.map(w => (
              <div
                key={w.name}
                onClick={() => setSelected(w.name)}
                style={{
                  display: "flex", alignItems: "center", gap: "12px", padding: "14px 16px",
                  border: `1.5px solid ${selected === w.name ? T.blue : T.border}`,
                  borderRadius: "10px", cursor: "pointer",
                  background: selected === w.name ? "#EBF2FF" : "#fff",
                  transition: "all .15s",
                }}
              >
                <div style={st.iconBox("#EBF2FF", 36)}>{w.icon}</div>
                <div>
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: T.navy }}>{w.name}</div>
                  <div style={{ fontSize: "12px", color: T.muted }}>{w.desc}</div>
                </div>
                {selected === w.name && <span style={{ marginLeft: "auto", color: T.blue, fontWeight: 800, fontSize: "16px" }}>Selected</span>}
              </div>
            ))
          }
        </div>
        <div style={{ padding: "14px 22px", borderTop: `1.5px solid #EBECF0`, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button style={st.btnGhost} onClick={onClose}>Cancel</button>
          <button
            style={st.btnBlue(!selected)}
            onClick={() => { if (selected) { onAdd(selected); onClose() } }}
          >Add Widget</button>
        </div>
      </div>
    </div>
  )
}

/* ══════════════════════════════════════════════════
   WIDGET CARD
══════════════════════════════════════════════════ */
function WidgetCard({ widget, data, onToggle, onSave, onDelete, onChange }) {
  const [savedState, setSavedState] = useState(false) // "idle"|true
  const meta = WIDGET_META[widget.widgetName] || {
    icon: "W", accent: T.blue, accentBg: "#EDF6FF",
    label: widget.widgetName, desc: "", editable: true, removable: true,
  }
  const isOn = data?.isVisible

  const handleSave = async () => {
    await onSave(widget)
    setSavedState(true)
    setTimeout(() => setSavedState(false), 2200)
  }

  const renderEditor = () => {
    const cfg = data?.config || {}
    const upd = (newCfg) => onChange(widget._id, { ...data, config: newCfg })
    switch (widget.widgetName) {
      case "Compliances": return <CompliancesEditor config={cfg} onChange={upd} />
      case "Controls": return <ControlsEditor config={cfg} onChange={upd} />
      case "FAQ": return <FaqEditor config={cfg} onChange={upd} />
      case "Subprocessors": return <SubprocessorsEditor config={cfg} onChange={upd} />
      default: return null
    }
  }

  return (
    <div style={st.card(isOn)}>
      {/* header */}
      <div style={st.cardHd}>
        <div style={st.row(12)}>
          <div style={st.iconBox(meta.accentBg)}>{meta.icon}</div>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: T.navy, letterSpacing: "-.2px" }}>
              {meta.label || widget.widgetName}
            </div>
            <div style={{ fontSize: "12px", color: T.muted, marginTop: "2px" }}>{meta.desc}</div>
          </div>
        </div>

        <div style={{ ...st.row(10), flexWrap: "wrap" }}>
          <Toggle on={isOn} onChange={() => onToggle(widget._id, isOn, widget.widgetName)} />
          {isOn && meta.editable && (
            <button
              style={{
                ...st.btnBlue(false),
                background: savedState ? T.green : T.blue,
                minWidth: "120px", transition: "background .3s",
              }}
              onClick={handleSave}
            >
              {savedState ? "Saved" : "Save changes"}
            </button>
          )}
          {meta.removable && (
            <button style={st.btnDanger} onClick={() => onDelete(widget._id)}>Remove widget</button>
          )}
        </div>
      </div>

      {/* editor */}
      {isOn && meta.editable && (
        <div style={st.edBody}>{renderEditor()}</div>
      )}

      {/* read-only note */}
      {isOn && !meta.editable && (
        <div style={st.readonlyBanner}>Content is managed elsewhere and cannot be edited here.</div>
      )}

      {/* hidden banner */}
      {!isOn && (
        <div style={st.hiddenBanner}>
          This section is hidden from the public Trust Center page.
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════ */
// export default function Widgets() {
//   const [widgets, setWidgets] = useState([])
//   const [formData, setFormData] = useState({})
//   const [loading, setLoading] = useState(true)
//   const [showAdd, setShowAdd] = useState(false)

//   const fetchWidgets = async () => {
//     try {
//       setLoading(true)
//       const res = await api.get("/widgets")
//       setWidgets(res.data)
//       const map = {}
//       res.data.forEach(w => { map[w._id] = { ...w } })
//       setFormData(map)
//     } catch (err) {
//       console.error(err)
//     } finally {
//       setLoading(false)
//     }
//   }

//   useEffect(() => { fetchWidgets() }, [])

//   const handleToggle = (id, cur) => setFormData(p => ({ ...p, [id]: { ...p[id], isVisible: !cur } }))
//   const handleChange = (id, nxt) => setFormData(p => ({ ...p, [id]: nxt }))

//   const handleSave = async (widget) => {
//     try {
//       await api.put(`/widgets/${widget._id}`, { ...formData[widget._id], updatedAt: new Date() })
//       fetchWidgets()
//     } catch (e) { alert(`Error saving ${widget.widgetName}`); console.error(e) }
//   }

//   const handleDelete = async (id) => {
//     if (!window.confirm("Remove this widget from the Trust Center?")) return
//     try {
//       await api.delete(`/admin/widgets/${id}`)
//       fetchWidgets()
//     } catch (e) { alert("Error removing widget"); console.error(e) }
//   }

//   const handleAdd = async (widgetName) => {
//     const defaults = {
//       Compliances: { config: { items: ["iso27001-2022", "iso27701", "soc2", "gdpr", "ccpa", "iso9001"], urls: {}, customItems: [] } },
//       TrustedBy: { config: { companies: DEFAULT_COMPANIES } },
//       Controls: {
//         config: {
//           categories: [
//             { icon: "🛡️", bg: "#EFF6FF", title: "Product security", items: ["Production System User Review", "Vulnerability Remediation Process"] },
//             { icon: "🔒", bg: "#F0FDF4", title: "Data security", items: ["Identity Validation", "Production Databases Access Restriction"] },
//             { icon: "🌐", bg: "#FDF4FF", title: "Network security", items: ["Impact analysis", "Limit Network Connections"] },
//           ]
//         }
//       },
//     }
//     try {
//       await api.post("/admin/widgets", { widgetName, isVisible: true, isEditable: true, ...(defaults[widgetName] || {}) })
//       fetchWidgets()
//     } catch (e) { alert("Error adding widget"); console.error(e) }
//   }

//   if (loading) return (
//     <AdminLayout title="Widget Manager">
//       <div style={{ textAlign: "center", padding: "80px 0", color: T.faint, fontSize: "14px", fontFamily: T.font }}>
//         Loading widgets…
//       </div>
//     </AdminLayout>
//   )

//   const existingNames = widgets.map(w => w.widgetName)
//   const visibleWidgets = widgets.filter(w => !HIDDEN_WIDGETS.includes(w.widgetName))
//   const canAdd = ADDABLE_WIDGETS.some(w => !existingNames.includes(w.name))
//   const visibleCount = visibleWidgets.filter(w => formData[w._id]?.isVisible).length

//   return (
//     <AdminLayout
//       title="Widget Manager"
//       description="Control which sections appear on the Trust Center and configure their content."
//     >
//       {/* top bar */}
//       <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" }}>
//         <div style={{ fontSize: "13px", color: T.muted, fontFamily: T.font }}>
//           {visibleCount} of {visibleWidgets.length} sections visible
//         </div>
//         {canAdd && (
//           <button style={{ ...st.btnBlue(false), display: "flex", alignItems: "center", gap: "6px" }} onClick={() => setShowAdd(true)}>
//             + Add Widget
//           </button>
//         )}
//       </div>

//       {/* cards */}
//       <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
//         {visibleWidgets.map(widget => (
//           <WidgetCard
//             key={widget._id}
//             widget={widget}
//             data={formData[widget._id]}
//             onToggle={handleToggle}
//             onSave={handleSave}
//             onDelete={handleDelete}
//             onChange={handleChange}
//           />
//         ))}
//       </div>

//       {showAdd && (
//         <AddWidgetModal
//           existingNames={existingNames}
//           onAdd={handleAdd}
//           onClose={() => setShowAdd(false)}
//         />
//       )}
//     </AdminLayout>
//   )
// }

// ... ALL YOUR IMPORTS + CODE ABOVE REMAINS SAME

export default function Widgets() {
  const { toast, confirm } = useAdminFeedback()
  const [widgets, setWidgets] = useState([])
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)

  const fetchWidgets = async () => {
    try {
      setLoading(true)
      const res = await api.get("/widgets")
      setWidgets(res.data)
      const map = {}
      res.data.forEach(w => { map[w._id] = { ...w } })
      setFormData(map)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchWidgets() }, [])

  const handleToggle = async (id, cur, widgetName) => {
    const turningOn = !cur
    try {
      setFormData(p => ({ ...p, [id]: { ...p[id], isVisible: turningOn } }))
      await api.put(`/widgets/${id}`, { ...formData[id], isVisible: turningOn })
    } catch (e) {
      toast.error("Error updating widget visibility")
      console.error(e)
      setFormData(p => ({ ...p, [id]: { ...p[id], isVisible: cur } }))
    }
  }

  const handleChange = (id, nxt) =>
    setFormData(p => ({ ...p, [id]: nxt }))

  const handleSave = async (widget) => {
    try {
      await api.put(`/widgets/${widget._id}`, {
        ...formData[widget._id],
      })
      fetchWidgets()
      toast.success(`${widget.widgetName} saved`)
    } catch (e) {
      toast.error(`Error saving ${widget.widgetName}`)
      console.error(e)
    }
  }

  const handleDelete = async (id) => {
    const ok = await confirm({ message: "Remove this widget from the Trust Center?", danger: true, confirmLabel: "Remove" })
    if (!ok) return
    try {
      await api.delete(`/admin/widgets/${id}`)
      fetchWidgets()
      toast.success("Widget removed")
    } catch (e) {
      toast.error("Error removing widget")
      console.error(e)
    }
  }

  const handleAdd = async (widgetName) => {
    const defaults = {
      Compliances: {
        config: {
          items: [],
        }
      },
      Controls: {
        config: {
          categories: [
            {
              icon: "",
              bg: "#EFF6FF",
              title: "Product security",
              items: ["Production System User Review", "Vulnerability Remediation Process"]
            },
            {
              icon: "",
              bg: "#F0FDF4",
              title: "Data security",
              items: ["Identity Validation", "Production Databases Access Restriction"]
            },
            {
              icon: "",
              bg: "#FDF4FF",
              title: "Network security",
              items: ["Impact analysis", "Limit Network Connections"]
            }
          ]
        }
      },
      FAQ: {
        config: {
          items: [...STATIC_TC.faqs],
        }
      },
      Subprocessors: {
        config: {
          items: [...STATIC_TC.subprocessors],
        }
      },
    }

    try {
      await api.post("/admin/widgets", {
        widgetName,
        isVisible: true,
        isEditable: true,
        ...(defaults[widgetName] || {})
      })
      fetchWidgets()
      toast.success(`${widgetName} widget added`)
    } catch (e) {
      toast.error("Error adding widget")
      console.error(e)
    }
  }

  if (loading) return (
    <AdminLayout title="Widget Manager">
      <div style={{
        textAlign: "center",
        padding: "80px 0",
        color: T.faint,
        fontSize: "14px",
        fontFamily: T.font
      }}>
        Loading widgets…
      </div>
    </AdminLayout>
  )

  const existingNames = widgets.map(w => w.widgetName)
  const visibleWidgets = widgets.filter(w => !HIDDEN_WIDGETS.includes(w.widgetName))
  const canAdd = ADDABLE_WIDGETS.some(w => !existingNames.includes(w.name))

  // ✅ DEDUPLICATION: Keep only the first instance of each widget type
  const deduplicatedWidgets = []
  const seenWidgetNames = new Set()
  for (const widget of visibleWidgets) {
    if (!seenWidgetNames.has(widget.widgetName)) {
      deduplicatedWidgets.push(widget)
      seenWidgetNames.add(widget.widgetName)
    }
  }

  const visibleCount = deduplicatedWidgets.filter((w) => formData[w._id]?.isVisible).length

  // ✅ SORTING LOGIC ADDED HERE
  const ORDER = ["Controls", "Compliances", "Subprocessors", "FAQ"]

  const sortedWidgets = [...deduplicatedWidgets].sort(
    (a, b) => {
      const aIndex = ORDER.indexOf(a.widgetName)
      const bIndex = ORDER.indexOf(b.widgetName)

      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
    }
  )

  return (
    <AdminLayout
      title="Widget Manager"
      description="Control which sections appear on the Trust Center and configure their content."
    >
      {/* top bar */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "10px"
      }}>
        <div style={{
          fontSize: "13px",
          color: T.muted,
          fontFamily: T.font
        }}>
          {visibleCount} of {visibleWidgets.length} sections visible
        </div>

        {canAdd && (
          <button
            style={{ ...st.btnBlue(false), display: "flex", alignItems: "center", gap: "6px" }}
            onClick={() => setShowAdd(true)}
          >
            + Add Widget
          </button>
        )}
      </div>

      {/* cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        {sortedWidgets.map(widget => (
          <WidgetCard
            key={widget._id}
            widget={widget}
            data={formData[widget._id]}
            onToggle={handleToggle}
            onSave={handleSave}
            onDelete={handleDelete}
            onChange={handleChange}
          />
        ))}
      </div>

      {showAdd && (
        <AddWidgetModal
          existingNames={existingNames}
          onAdd={handleAdd}
          onClose={() => setShowAdd(false)}
        />
      )}
    </AdminLayout>
  )
}